import { useSelector } from "react-redux";
import { selectSubGroupConfiguration } from "app/slices/admin.slice";
import Button from "common/Buttons/Button";
import React, { useState } from "react";
import {
  SUB_GROUP_CONFIGURATION_KICK_EXPIRED_USERS,
  SUB_GROUP_CONFIGURATION_KICK_UNAUTHORIZED_USERS,
} from "common/constants";
import styled, { css, useTheme } from "styled-components";
import Switch from "common/Buttons/Switch";
import { openMessage } from "utils/misc";
import { SubGroupConfiguration, LanguageNameSpaces } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import GroupSelect from "features/admin/group/GroupSelect.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { FiltersWrapper } from "features/admin/group/users/styles.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface ConfigurationProps {
  subGroupID: number;
  mobileActions?: React.JSX.Element;
}

const Configuration = ({ subGroupID, mobileActions }: ConfigurationProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const subGroupConfiguration = useSelector(
    selectSubGroupConfiguration(subGroupID)
  );
  const [modifiedConfiguration, setModifiedConfiguration] = useState<
    Partial<SubGroupConfiguration>
  >({});
  const [modifiedPosts, setModifiedPosts] = useState<Record<number, boolean>>(
    {}
  );
  const subGroupPosts = subGroupConfiguration?.posts;
  const [updateSubGroupConfiguration, { isLoading }] =
    sharedLib.sharedApi.admin.useUpdateSubGroupConfigurationMutation();

  const handleParameterChange = (
    key: keyof SubGroupConfiguration,
    value: boolean
  ) => {
    setModifiedConfiguration({
      ...modifiedConfiguration!,
      [key]: value,
    });
  };

  const handlePostsChange = (key: number, value: boolean) => {
    setModifiedPosts({
      ...modifiedPosts,
      [key]: value,
    });
  };

  const checkAnyParameterModified = () => {
    const configurationProps = Object.keys(modifiedConfiguration) as Array<
      keyof SubGroupConfiguration
    >;
    const configurationModified = configurationProps.some(
      (parameterKey) =>
        Boolean(modifiedConfiguration[parameterKey]) !==
        Boolean(subGroupConfiguration?.[parameterKey])
    );
    const postsModified = subGroupPosts?.some((post) => {
      const postId = post.id;
      return (
        postId in modifiedPosts &&
        Boolean(post.is_active) !== Boolean(modifiedPosts[postId])
      );
    });
    return configurationModified || postsModified;
  };

  const handleSaveConfiguration = () => {
    updateSubGroupConfiguration({
      sub_group_id: subGroupID,
      ...modifiedConfiguration,
      posts: modifiedPosts,
    })
      .unwrap()
      .then(() => {
        setModifiedConfiguration({});
        openMessage(t("configuration.saveSuccess"), "success");
      })
      .catch((error) =>
        openMessage(
          error.message ? error.message : t("configuration.saveError"),
          "error"
        )
      );
  };

  const getModifiedConfigurationValue = (key: keyof SubGroupConfiguration) =>
    key in modifiedConfiguration
      ? modifiedConfiguration[key]
      : subGroupConfiguration?.[key];

  const sortedSubGroupPosts = subGroupPosts
    ?.slice()
    .sort((a, b) => a.num_days - b.num_days);

  // New look: two settings panels (title in the header, one switch per
  // row) and the form's one primary action under them on the right.
  if (appTheme.isNewLook) {
    const settingRow = (
      key: string | number,
      name: string,
      checked: boolean,
      onChange: (checked: boolean) => void
    ) => (
      <NewLookRow key={key}>
        <NewLookRowName id={`group-setting-${key}`}>{name}</NewLookRowName>
        <Switch
          checked={checked}
          onChange={onChange}
          aria-labelledby={`group-setting-${key}`}
        />
      </NewLookRow>
    );
    return (
      <>
        {isBottomNavigator && (
          <FiltersWrapper>
            <GroupSelect size="middle" />
          </FiltersWrapper>
        )}
        <NewLookWrapper $isPhone={isBottomNavigator}>
          {isBottomNavigator && mobileActions}
          <Panel
            title={<WrappingTitle>{t("configuration.title")}</WrappingTitle>}
            flush={true}
          >
            {settingRow(
              SUB_GROUP_CONFIGURATION_KICK_EXPIRED_USERS,
              t("configuration.kickExpiredUsers"),
              Boolean(
                getModifiedConfigurationValue(
                  SUB_GROUP_CONFIGURATION_KICK_EXPIRED_USERS
                )
              ),
              (checked) =>
                handleParameterChange(
                  SUB_GROUP_CONFIGURATION_KICK_EXPIRED_USERS,
                  checked
                )
            )}
            {settingRow(
              SUB_GROUP_CONFIGURATION_KICK_UNAUTHORIZED_USERS,
              t("configuration.kickUnauthorizedUsers"),
              Boolean(
                getModifiedConfigurationValue(
                  SUB_GROUP_CONFIGURATION_KICK_UNAUTHORIZED_USERS
                )
              ),
              (checked) =>
                handleParameterChange(
                  SUB_GROUP_CONFIGURATION_KICK_UNAUTHORIZED_USERS,
                  checked
                )
            )}
          </Panel>
          <Panel
            title={
              <WrappingTitle>{t("configuration.alertTitle")}</WrappingTitle>
            }
            flush={true}
          >
            {sortedSubGroupPosts?.map((post) =>
              settingRow(
                post.id,
                t("configuration.alertBefore", { count: post.num_days }),
                post.id in modifiedPosts
                  ? modifiedPosts[post.id]
                  : post.is_active,
                (checked) => handlePostsChange(post.id, checked)
              )
            )}
          </Panel>
          <NewLookSaveRow $isPhone={isBottomNavigator}>
            <Button
              onClick={handleSaveConfiguration}
              disabled={!checkAnyParameterModified()}
              type="primary"
              loading={isLoading}
              block={isBottomNavigator}
            >
              {t("configuration.save")}
            </Button>
          </NewLookSaveRow>
        </NewLookWrapper>
      </>
    );
  }

  return (
    <>
      {isBottomNavigator && (
        <FiltersWrapper>
          <GroupSelect size="middle" />
        </FiltersWrapper>
      )}
      <div
        style={{
          color: appTheme.colors.textColor,
          padding: isBottomNavigator ? 12 : 0,
        }}
      >
        {isBottomNavigator && mobileActions}
        <p style={{ margin: "8px 0 16px 0" }}>{t("configuration.title")}</p>
        <ParameterWrapper>
          <ParameterName>{t("configuration.kickExpiredUsers")}</ParameterName>
          <Switch
            checked={Boolean(
              getModifiedConfigurationValue(
                SUB_GROUP_CONFIGURATION_KICK_EXPIRED_USERS
              )
            )}
            onChange={(checked: boolean) =>
              handleParameterChange(
                SUB_GROUP_CONFIGURATION_KICK_EXPIRED_USERS,
                checked
              )
            }
          />
        </ParameterWrapper>
        <ParameterWrapper>
          <ParameterName>
            {t("configuration.kickUnauthorizedUsers")}
          </ParameterName>
          <Switch
            checked={Boolean(
              getModifiedConfigurationValue(
                SUB_GROUP_CONFIGURATION_KICK_UNAUTHORIZED_USERS
              )
            )}
            onChange={(checked: boolean) =>
              handleParameterChange(
                SUB_GROUP_CONFIGURATION_KICK_UNAUTHORIZED_USERS,
                checked
              )
            }
          />
        </ParameterWrapper>
        <p style={{ margin: "32px 0 16px 0" }}>
          {t("configuration.alertTitle")}
        </p>
        {sortedSubGroupPosts?.map((post) => {
          return (
            <ParameterWrapper key={post.id}>
              <ParameterName>
                {t("configuration.alertDays", { count: post.num_days })}
              </ParameterName>
              <Switch
                checked={
                  post.id in modifiedPosts
                    ? modifiedPosts[post.id]
                    : post.is_active
                }
                onChange={(checked: boolean) =>
                  handlePostsChange(post.id, checked)
                }
              />
            </ParameterWrapper>
          );
        })}
        <Button
          style={{
            marginTop: "24px",
          }}
          onClick={handleSaveConfiguration}
          disabled={!checkAnyParameterModified()}
          type="primary"
          shape="round"
          loading={isLoading}
        >
          {t("configuration.save")}
        </Button>
      </div>
    </>
  );
};

export default React.memo(Configuration);

const NewLookWrapper = styled.div<{ $isPhone?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
  ${(p) =>
    p.$isPhone &&
    css`
      gap: 12px;
      max-width: none;
      padding: 12px 16px 0;
    `}
`;
/* Desktop: Save on the right under the panels. Phones: full width and
   sticky, so it stays reachable at the bottom of the screen. */
const NewLookSaveRow = styled.div<{ $isPhone?: boolean }>`
  display: flex;
  justify-content: flex-end;
  ${(p) =>
    p.$isPhone &&
    css`
      position: sticky;
      bottom: 0;
      margin: 0 -16px;
      padding: 12px 16px;
      background: ${getNewLookTokens(p.theme).canvas};
      border-top: 1px solid ${getNewLookTokens(p.theme).hairline};
      .ant-btn {
        height: 44px;
      }
    `}
`;
const NewLookRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 60px;
  padding: 8px 20px;
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
// Long titles wrap on phones instead of running out of the panel.
const WrappingTitle = styled.span`
  white-space: normal;
`;
const NewLookRowName = styled.div`
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const ParameterWrapper = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
  justify-content: space-between;
  max-width: 400px;
`;
const ParameterName = styled.div`
  width: 360px;
`;
